/**
 * Plugin Registration
 *
 * This file registers all available plugins with the plugin registry.
 * Import and register new plugins here.
 */

import { fetchWithBypass } from '../../platform/utils/fetch';
import { pluginRegistry } from './core/PluginRegistry';
import { aiInsightsPlugin } from './ai-insights';
import { cashFlowVisualizationPlugin } from './cash-flow-visualization';
import { socialDeepDivePlugin } from './social-deepdive';
import { universalImporterPlugin } from '@/plugins/csv-import/plugin';

import { pluginLogger } from '@/lib/platform/utils/logger';

/**
 * Register all plugins
 * Call this once during app initialization
 */
export async function registerAllPlugins() {
  pluginLogger.log('[Plugins] Registering all plugins...');

  try {
    // Register AI Insights plugin
    await pluginRegistry.register(aiInsightsPlugin);

    // Register Flow Visualization plugin
    await pluginRegistry.register(cashFlowVisualizationPlugin);

    // Register SocialDeepDive plugin
    await pluginRegistry.register(socialDeepDivePlugin);

    // Register Universal Importer plugin
    await pluginRegistry.register(universalImporterPlugin);

    // Register future plugins here
    // await pluginRegistry.register(pdfExportPlugin);
    // await pluginRegistry.register(excelExportPlugin);

    const stats = pluginRegistry.getStats();
    pluginLogger.log(`[Plugins] Registered ${stats.totalPlugins} plugin(s)`);
  } catch (error) {
    pluginLogger.error('[Plugins] Failed to register plugins:', error);
    throw error;
  }
}

/**
 * Initialize enabled plugins for an organization
 */
export async function initializePluginsForOrganization(organizationId: string) {
  pluginLogger.log(`[Plugins] Initializing plugins for organization: ${organizationId}`);

  try {
    // Load plugin settings from database
    const { data: settings } = await fetchWithBypass(
      '/api/plugin-settings?organizationId=' + organizationId
    ).then((r) => r.json());

    if (!settings || settings.length === 0) {
      pluginLogger.log(`[Plugins] No plugins configured for organization: ${organizationId}`);
      return;
    }

    // Enable each configured plugin
    for (const setting of settings) {
      if (setting.enabled) {
        try {
          await pluginRegistry.enable(setting.plugin_id, organizationId);
          pluginLogger.log(`[Plugins] Enabled plugin: ${setting.plugin_id}`);
        } catch (error) {
          pluginLogger.error(`[Plugins] Failed to enable plugin ${setting.plugin_id}:`, error);
        }
      }
    }

    const stats = pluginRegistry.getStats();
    pluginLogger.log(
      `[Plugins] Initialized plugins. ${stats.enabledPluginsCount} plugin(s) enabled`
    );
  } catch (error) {
    pluginLogger.error(
      `[Plugins] Failed to initialize plugins for organization ${organizationId}:`,
      error
    );
  }
}

/**
 * Health check all plugins
 */
export async function healthCheckAllPlugins() {
  pluginLogger.log('[Plugins] Running health checks...');

  const results = await pluginRegistry.healthCheckAll();

  for (const [pluginId, health] of results.entries()) {
    pluginLogger.log(`[Plugins] ${pluginId}: ${health.status} - ${health.message || 'OK'}`);
  }

  return results;
}
